import { create } from 'zustand';
import { DEFAULT_SETTINGS, type UserSettings } from '@cardhub/protocol';

const KEY = 'th.settings';

function load(): UserSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw) as Partial<UserSettings>;
      return { ...DEFAULT_SETTINGS, ...s, notifications: { ...DEFAULT_SETTINGS.notifications, ...s.notifications }, privacy: { ...DEFAULT_SETTINGS.privacy, ...s.privacy } };
    }
  } catch {
    /* ignore */
  }
  const lang = navigator.language?.startsWith('ne') ? 'ne' : navigator.language?.startsWith('hi') ? 'hi' : 'en';
  return { ...DEFAULT_SETTINGS, language: lang };
}

interface SettingsState {
  settings: UserSettings;
  set(patch: Partial<UserSettings>): void;
  replace(s: UserSettings): void;
}

export const useSettings = create<SettingsState>((set, get) => ({
  settings: load(),
  set(patch) {
    const next = {
      ...get().settings,
      ...patch,
      notifications: { ...get().settings.notifications, ...(patch.notifications ?? {}) },
      privacy: { ...get().settings.privacy, ...(patch.privacy ?? {}) },
    };
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    set({ settings: next });
  },
  replace(s) {
    try {
      localStorage.setItem(KEY, JSON.stringify(s));
    } catch {
      /* ignore */
    }
    set({ settings: s });
  },
}));

/** Applies visual preferences to the document root. */
export function applySettings(s: UserSettings): void {
  const root = document.documentElement;
  const theme = s.theme === 'system' ? (matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark') : s.theme;
  root.dataset.theme = theme;
  root.dataset.contrast = s.highContrast ? 'high' : 'normal';
  root.dataset.felt = s.tableFelt ?? 'emerald';
  root.dataset.motion = s.reducedMotion || s.graphics === 'low' ? 'reduced' : 'full';
  root.style.setProperty('--font-scale', String(s.fontScale));
  root.lang = s.language;
}
